/*
 * Daily counters — bumps a number inside an element's text each calendar day.
 *
 * Read from data-* attributes on the target element:
 *   data-counter-base             — value on day 0 (the baseline). Required
 *                                   unless daily-min/daily-max are both set.
 *   data-counter-growth           — amount added per day. Default 1.
 *   data-counter-min              — floor; value is clamped up to this. Default = base.
 *   data-counter-cap              — ceiling; value is clamped down to this. Default = none.
 *   data-counter-daily-min        — fluctuating mode: if this AND daily-max are
 *   data-counter-daily-max          set, the value is a deterministic pseudo-
 *                                   random number within [daily-min, daily-max],
 *                                   reseeded each calendar day. Same value all
 *                                   day; varies day to day; NOT monotonic.
 *                                   Overrides base/growth/min/cap. Good for a
 *                                   believable "sold last 24h"-style figure.
 *   data-counter-epoch            — ISO date "YYYY-MM-DD" treated as day 0
 *                                   for this counter. Defaults to the global
 *                                   2025-05-01 epoch.
 *   data-counter-pct-denominator  — if set, also recompute the leading "NN%"
 *                                   in the text as today / denominator, and
 *                                   sync --progress on the nearest ancestor
 *                                   carrying that CSS variable (e.g. the
 *                                   highlight row's yellow progress bar).
 *
 * Behavior:
 *   - Deterministic per calendar day (same value all day on every visit).
 *   - Strictly monotonic upward — tomorrow's value is always >= today's.
 *   - Replaces only the FIRST number in the element's text. Recognizes
 *     comma-formatted numbers ("11,235") and plain 3+ digit numbers ("273"),
 *     so a leading "83%" in the text is left alone unless pct-denominator
 *     is set, in which case the percent is replaced first.
 *   - New value is rendered with en-US thousands separators.
 *
 * The default base date is May 1 2025 — the rough deploy moment. Visitors
 * before that date land on the configured base value (no negative days).
 * Override per element with data-counter-epoch.
 */
(function () {
  var elements = document.querySelectorAll('[data-counter-base], [data-counter-daily-min]');
  if (!elements.length) return;

  var DEFAULT_EPOCH = Date.UTC(2025, 4, 1);
  var NUMBER_RE = /\d{1,3}(?:,\d{3})+|\d{3,}/;
  var PCT_RE = /\d+%/;
  var EPOCH_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

  // Deterministic 32-bit integer hash → float in [0, 1). Same input always
  // yields the same output, so seeding it with the day index gives a value
  // that's stable for a whole calendar day but jumps unpredictably day to day.
  function dayHash(n) {
    var x = (n + 1) >>> 0;
    x = (x ^ 61) ^ (x >>> 16);
    x = (x + (x << 3)) >>> 0;
    x = x ^ (x >>> 4);
    x = Math.imul(x, 0x27d4eb2d) >>> 0;
    x = x ^ (x >>> 15);
    return (x >>> 0) / 4294967296;
  }

  // Roll the counter over at UK local midnight (Europe/London auto-handles the
  // BST/GMT switch) rather than UTC midnight. We take today's London calendar
  // date and pin it to that date's UTC midnight, so the day difference against
  // the epoch (also a UTC-midnight calendar date) is an exact integer of days.
  function londonMidnightUTC(ms) {
    try {
      var ymd = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/London',
        year: 'numeric', month: '2-digit', day: '2-digit'
      }).format(new Date(ms));
      var m = EPOCH_RE.exec(ymd);
      if (m) return Date.UTC(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    } catch (e) { /* Intl/timeZone unsupported — fall back to UTC */ }
    return ms;
  }

  var nowMs = londonMidnightUTC(Date.now());

  elements.forEach(function (el) {
    var base = parseInt(el.dataset.counterBase, 10);
    var dailyMin = parseInt(el.dataset.counterDailyMin, 10);
    var dailyMax = parseInt(el.dataset.counterDailyMax, 10);
    var dailyMode = isFinite(dailyMin) && isFinite(dailyMax) && dailyMax >= dailyMin;
    if (!isFinite(base) && !dailyMode) return;
    var growth = parseFloat(el.dataset.counterGrowth || '1');
    var min = parseInt(el.dataset.counterMin != null ? el.dataset.counterMin : base, 10);
    var hasCap = el.dataset.counterCap != null;
    var cap = hasCap ? parseInt(el.dataset.counterCap, 10) : Infinity;

    var epoch = DEFAULT_EPOCH;
    var epochAttr = el.dataset.counterEpoch;
    if (epochAttr) {
      var m = EPOCH_RE.exec(epochAttr);
      if (m) epoch = Date.UTC(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    }
    var daysSince = Math.max(0, Math.floor((nowMs - epoch) / 86400000));

    var today;
    if (dailyMode) {
      // Pick a stable-per-day pseudo-random value within [dailyMin, dailyMax].
      today = dailyMin + Math.floor(dayHash(daysSince) * (dailyMax - dailyMin + 1));
    } else {
      today = base + Math.floor(daysSince * growth);
      if (today < min) today = min;
      if (today > cap) today = cap;
    }

    var formatted = today.toLocaleString('en-US');

    var denom = parseInt(el.dataset.counterPctDenominator, 10);
    var percentValue = null;
    if (isFinite(denom) && denom > 0) {
      percentValue = Math.floor((today / denom) * 100);
      var progressHost = el.closest('[style*="--progress"]');
      if (progressHost) {
        progressHost.style.setProperty('--progress', percentValue + '%');
      }
    }
    var pctDone = percentValue == null;

    // Walk descendants to find the first text node with a matching number.
    // If pct-denominator is set, also replace the first "NN%" along the way.
    var stack = [el];
    while (stack.length) {
      var node = stack.shift();
      for (var i = 0; i < node.childNodes.length; i++) {
        var child = node.childNodes[i];
        if (child.nodeType === 3) {
          var text = child.textContent;
          var changed = false;
          if (!pctDone && PCT_RE.test(text)) {
            text = text.replace(PCT_RE, percentValue + '%');
            pctDone = true;
            changed = true;
          }
          if (NUMBER_RE.test(text)) {
            child.textContent = text.replace(NUMBER_RE, formatted);
            return;
          }
          if (changed) {
            child.textContent = text;
          }
        } else if (child.nodeType === 1) {
          stack.push(child);
        }
      }
    }
  });
})();
